import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  Renderer2,
  ViewContainerRef,
  ComponentRef,
  NgZone,
  ComponentFactoryResolver
} from '@angular/core';
import { SpellCheckerService, SpellCheckResult } from '../../services/spell-checker/spell-checker.service';
import { SpellCheckSuggestionsComponent } from '../../components/spell-check-suggestions/spell-check-suggestions.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Directive({
  selector: '[aogSpellCheck]'
})
export class SpellCheckDirective implements OnInit, OnDestroy {
  @Input() spellCheckEnabled: boolean = true;
  @Input() debounceTime: number = 300; // milliseconds

  private textChange$ = new Subject<string>();
  private currentMisspellings: SpellCheckResult[] = [];
  private suggestionComponentRef: ComponentRef<SpellCheckSuggestionsComponent> | null = null;
  private activeWordElement: HTMLElement | null = null;
  private subscriptions: any[] = [];

  constructor(
    private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    private spellChecker: SpellCheckerService,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {}

  ngOnInit(): void {
    if (!this.spellCheckEnabled) {
      return;
    }

    // Subscribe to text changes with debounce
    const subscription = this.textChange$
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe(text => {
        this.checkSpelling(text);
      });

    this.subscriptions.push(subscription);

    // Initial check
    setTimeout(() => {
      this.checkSpelling(this.getElementValue());
    }, 500);
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.forEach(sub => {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
    this.hideSuggestions();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (!this.spellCheckEnabled) {
      return;
    }
    const value = this.getElementValue();
    
    // Immediately clear highlighting if text is empty
    if (!value || value.trim().length === 0) {
      this.clearMisspellings();
    }
    
    this.textChange$.next(value);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    // Hide suggestions when field loses focus (with delay to allow clicking suggestions)
    setTimeout(() => {
      // Check if user clicked on suggestions
      const activeElement = document.activeElement;
      if (activeElement && activeElement.closest('.spell-check-suggestions')) {
        return; // Don't hide if clicking on suggestions
      }
      this.hideSuggestions();
    }, 300);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Hide suggestions on Escape key
    if (event.key === 'Escape') {
      this.hideSuggestions();
    }
  }

  @HostListener('contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    if (!this.spellCheckEnabled) {
      return;
    }

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const text = this.getElementValue();
    const selectionStart = target.selectionStart || 0;
    
    // Find the word at the cursor position
    const wordAtCursor = this.getWordAtPosition(text, selectionStart);
    
    if (wordAtCursor) {
      // Check if this word is misspelled
      const misspelling = this.currentMisspellings.find(m => 
        m.position.start <= selectionStart && m.position.end >= selectionStart
      );
      
      if (misspelling && misspelling.suggestions.length > 0) {
        event.preventDefault();
        this.showSuggestions(misspelling.word, misspelling.suggestions, misspelling.position);
      }
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    // Don't hide suggestions on click - let blur handle it
    // This allows users to click on suggestions
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Hide suggestions when clicking outside the input and suggestions
    if (this.suggestionComponentRef) {
      const target = event.target as HTMLElement;
      const inputElement = this.el.nativeElement;
      const suggestionElement = this.suggestionComponentRef.location.nativeElement;
      
      if (!inputElement.contains(target) && !suggestionElement.contains(target)) {
        this.hideSuggestions();
      }
    }
  }

  private getWordAtPosition(text: string, position: number): { word: string; start: number; end: number } | null {
    if (position < 0 || position > text.length) {
      return null;
    }

    // Find word boundaries
    let start = position;
    let end = position;

    // Move start backwards to find word start
    while (start > 0 && /\w/.test(text[start - 1])) {
      start--;
    }

    // Move end forwards to find word end
    while (end < text.length && /\w/.test(text[end])) {
      end++;
    }

    if (start === end) {
      return null;
    }

    const word = text.substring(start, end);
    return { word, start, end };
  }

  private getElementValue(): string {
    return this.el.nativeElement.value || '';
  }

  private async checkSpelling(text: string): Promise<void> {
    // Clear misspellings if text is empty or only whitespace
    if (!text || text.trim().length === 0) {
      this.clearMisspellings();
      this.hideSuggestions();
      // Remove red border class
      this.renderer.removeClass(this.el.nativeElement, 'has-spelling-errors');
      return;
    }

    try {
      const results = await this.spellChecker.checkText(text);
      this.currentMisspellings = results;
      this.highlightMisspellings(text, results);
      
      // Automatically show suggestions for the first misspelling
      if (results.length > 0) {
        const firstMisspelling = results[0];
        if (firstMisspelling.suggestions && firstMisspelling.suggestions.length > 0) {
          // Show suggestions automatically after a short delay
          setTimeout(() => {
            this.showSuggestions(
              firstMisspelling.word,
              firstMisspelling.suggestions,
              firstMisspelling.position
            );
          }, 500);
        }
      } else {
        // Hide suggestions if no misspellings
        this.hideSuggestions();
      }
    } catch (error) {
      console.error('Spell check error:', error);
      // On error, clear highlighting
      this.clearMisspellings();
      this.renderer.removeClass(this.el.nativeElement, 'has-spelling-errors');
    }
  }

  private highlightMisspellings(text: string, misspellings: SpellCheckResult[]): void {
    // For input/textarea, we can't directly highlight text
    // Instead, we'll show suggestions on hover/click of misspelled words
    // This is a simplified approach - for full highlighting, you'd need contenteditable divs
    
    // Store misspellings for later use
    this.currentMisspellings = misspellings;
    
    // Add or remove visual indicator (red border) based on misspellings
    if (misspellings && misspellings.length > 0) {
      this.renderer.addClass(this.el.nativeElement, 'has-spelling-errors');
    } else {
      this.renderer.removeClass(this.el.nativeElement, 'has-spelling-errors');
    }
  }

  private clearMisspellings(): void {
    this.currentMisspellings = [];
    this.hideSuggestions();
    // Remove red border class when clearing misspellings
    this.renderer.removeClass(this.el.nativeElement, 'has-spelling-errors');
  }

  /**
   * Show suggestions for a word at a specific position
   */
  showSuggestions(word: string, suggestions: string[], position: { start: number; end: number }): void {
    this.hideSuggestions();

    if (!suggestions || suggestions.length === 0) {
      return;
    }

    this.ngZone.run(() => {
      try {
        // Create suggestion component using ComponentFactoryResolver (Angular 8)
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(SpellCheckSuggestionsComponent);
        const componentRef = this.viewContainerRef.createComponent(componentFactory);
        this.suggestionComponentRef = componentRef;

        // Set input data
        componentRef.instance.word = word;
        componentRef.instance.suggestions = suggestions;
        
        // Subscribe to the EventEmitter
        const subscription = componentRef.instance.onSuggestionClick.subscribe((suggestion: string) => {
          this.replaceWord(word, suggestion, position);
          this.hideSuggestions();
        });
        
        // Store subscription to clean up later
        this.subscriptions.push(subscription);

        // Position the suggestions near the input field
        this.positionSuggestions(componentRef.location.nativeElement, position);
      } catch (error) {
        console.error('Error creating spell check suggestions component:', error);
      }
    });
  }

  private positionSuggestions(element: HTMLElement, position: { start: number; end: number }): void {
    const inputElement = this.el.nativeElement;
    const rect = inputElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Position suggestions below the input field for better visibility
    this.renderer.setStyle(element, 'position', 'absolute');
    this.renderer.setStyle(element, 'top', `${rect.bottom + scrollTop + 5}px`);
    this.renderer.setStyle(element, 'left', `${rect.left + scrollLeft}px`);
    this.renderer.setStyle(element, 'z-index', '10000');
    this.renderer.setStyle(element, 'max-width', `${Math.max(rect.width, 300)}px`);
    this.renderer.setStyle(element, 'min-width', '200px');
    
    // Append to body for proper positioning
    document.body.appendChild(element);
    
    // Adjust position if suggestions go off screen
    setTimeout(() => {
      const suggestionRect = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Adjust horizontal position
      if (suggestionRect.right > viewportWidth) {
        const newLeft = viewportWidth - suggestionRect.width - 10;
        this.renderer.setStyle(element, 'left', `${newLeft + scrollLeft}px`);
      }
      if (suggestionRect.left < 0) {
        this.renderer.setStyle(element, 'left', `${10 + scrollLeft}px`);
      }
      
      // Adjust vertical position - show above if below goes off screen
      if (suggestionRect.bottom > viewportHeight) {
        const newTop = rect.top + scrollTop - suggestionRect.height - 5;
        this.renderer.setStyle(element, 'top', `${newTop}px`);
      }
      if (suggestionRect.top < 0) {
        this.renderer.setStyle(element, 'top', `${10 + scrollTop}px`);
      }
    }, 0);
  }

  private replaceWord(oldWord: string, newWord: string, position: { start: number; end: number }): void {
    const currentValue = this.getElementValue();
    const before = currentValue.substring(0, position.start);
    const after = currentValue.substring(position.end);
    const newValue = before + newWord + after;

    // Update the input value
    this.el.nativeElement.value = newValue;
    
    // Trigger input event for Angular forms
    const event = new Event('input', { bubbles: true });
    this.el.nativeElement.dispatchEvent(event);

    // Update cursor position
    const newCursorPos = position.start + newWord.length;
    if (this.el.nativeElement.setSelectionRange) {
      this.el.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
    }

    // Re-check spelling
    this.textChange$.next(newValue);
  }

  private hideSuggestions(): void {
    if (this.suggestionComponentRef) {
      // Clean up any subscriptions related to this component
      // The subscriptions array will be cleaned up in ngOnDestroy
      this.suggestionComponentRef.destroy();
      this.suggestionComponentRef = null;
    }
  }

  /**
   * Get misspellings for the current text
   * Can be called from parent component
   */
  getMisspellings(): SpellCheckResult[] {
    return this.currentMisspellings;
  }

  /**
   * Manually trigger spell check
   */
  async triggerSpellCheck(): Promise<void> {
    const text = this.getElementValue();
    await this.checkSpelling(text);
  }
}

